import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { vehiclesService, readVehicleListCache } from "@/services/vehicles.service";
import { positionsService } from "@/services/positions.service";
import { alertsService } from "@/services/alerts.service";
import {
  alertRulesService,
  type ActionKind,
  type AlertRule,
  type AlertType,
  type AlertRuleConfig,
  type AlertEventsPageParams,
  type CreateAlertRuleInput,
  type CreateGeofenceAlertRuleBatchInput,
  type UpdateAlertRuleInput,
} from "@/services/alert-rules.service";
import { parseWkt } from "@/lib/wkt";
import type { Geofence } from "@/types";
import { usersService, readUsersListCache } from "@/services/users.service";
import { isProtectedServiceAccount } from "@/lib/protected-accounts";
import {
  gpsDriveUsersService,
  type AccountType,
  type CreateResellerInput,
} from "@/services/gpsdrive-users.service";
import {
  gpsDriveUsersSummaryService,
  readUsersSummaryCache,
  type UserRowSummary,
} from "@/services/gpsdrive-users-summary.service";
import {
  gpsDriveRolesService,
  type GpsDriveRole,
  type RoleScope,
} from "@/services/gpsdrive-roles.service";
import {
  gpsDriveUserPermissionsService,
  type PermissionCategoryState,
} from "@/services/gpsdrive-user-permissions.service";
import {
  gpsDriveClientProfileService,
  type UpdateGpsDriveClientProfileInput,
} from "@/services/gpsdrive-client-profile.service";
import {
  gpsDriveAsaasIntegrationService,
  type UpdateAsaasIntegrationInput,
} from "@/services/gpsdrive-asaas-integration.service";
import { gpsDriveWhatsappIntegrationService } from "@/services/gpsdrive-whatsapp-integration.service";
import {
  gpsDriveCommunicationFailureService,
  type TestSendCommunicationFailureInput,
  type UpdateCommunicationFailureInput,
} from "@/services/gpsdrive-communication-failure.service";
import {
  gpsDriveAdminBrandingService,
  type UpdateAdminBrandingInput,
} from "@/services/gpsdrive-admin-branding.service";
import {
  gpsDriveBillingService,
  type CreateInvoiceInput,
  type CreateSubscriptionInput,
  type UpdateInvoiceInput,
} from "@/services/gpsdrive-billing.service";
import {
  gpsDriveClientDocumentsService,
  type ClientDocumentCategory,
} from "@/services/gpsdrive-client-documents.service";
import {
  gpsDriveContractsService,
  type UpsertContractSignatoryInput,
  type UpsertContractTemplateInput,
} from "@/services/gpsdrive-contracts.service";
import {
  gpsDriveSubUsersService,
  readCompanySubUserTraccarIdsCache,
  type CreateSubUserInput,
  type UpdateSubUserInput,
} from "@/services/gpsdrive-sub-users.service";
import {
  gpsDriveWelcomeMessageService,
  type UpdateWelcomeMessageInput,
} from "@/services/gpsdrive-welcome-message.service";
import { gpsDrivePlansService, type UpsertPlanInput } from "@/services/gpsdrive-plans.service";
import { gt06CommandsService, type UpsertGt06CommandInput } from "@/services/gt06-commands.service";
import { gpsDriveTrackerProtocolsService } from "@/services/gpsdrive-tracker-protocols.service";
import { trackerProtocolsKey } from "@/hooks/queries/platform";
import {
  gpsDriveAssistanceService,
  type CreateAssistanceRequestInput,
  type CreateServiceTypeInput,
  type UpdateServiceTypeInput,
  type UpsertServiceGroupInput,
} from "@/services/gpsdrive-assistance.service";
import {
  gpsDriveClientContractService,
  type UpdateClientContractInput,
} from "@/services/gpsdrive-client-contract.service";
import {
  activityLogService,
  type ActivityLogFilters,
} from "@/services/gpsdrive-activity-log.service";
import { gpsDriveUserSessionsService } from "@/services/gpsdrive-user-sessions.service";
import { gpsDriveUserFcmTokensService } from "@/services/gpsdrive-user-fcm-tokens.service";
import { gpsDriveQuickAccessService } from "@/services/gpsdrive-quick-access.service";
import { permissionsService } from "@/services/permissions.service";
import { vehiclePermissionsService } from "@/services/vehicle-permissions.service";
import { commandsService } from "@/services/commands.service";
import { smsSendService, type SendSmsCommandInput } from "@/services/sms-send.service";
import {
  defaultProtocolForTrackerModel,
  isSmsOnlyTrackerModel,
  type SmsFallbackContext,
} from "@/lib/send-block-command-with-sms-fallback";
import { geofencesService } from "@/services/geofences.service";
import { fleetCameraService } from "@/services/fleet-camera.service";
import { notificationsService } from "@/services/notifications.service";
import { reportsService } from "@/services/reports.service";
import { vehicleMetadataService } from "@/services/vehicle-metadata.service";
import { gpsDriveVehicleTrashService } from "@/services/gpsdrive-vehicle-trash.service";
import { gpsDriveUserDeletionsService } from "@/services/gpsdrive-user-deletions.service";
import {
  historyDeletionsService,
  type CreateHistoryDeletionInput,
} from "@/services/gpsdrive-history-deletions.service";
import { GPSDRIVE_API_URL, getGpsDriveAccessToken } from "@/lib/api/gpsdrive-client";
import type { CommandType } from "@/types";
import type { UpdateVehicleMetadataInput, VehicleImageUrls } from "@/types/vehicle-metadata";
import { mergeVehicleData } from "@/lib/vehicle-overrides";

// Cache local só de leitura otimista (nunca fonte de verdade): a URL da foto
// do veículo é servida sem auth pelo backend (ver storage.controller.ts —
// só upload/delete exigem authGuard), então não há dado sensível aqui, só
// performance. Sem isso, todo F5 mostra o ícone genérico por alguns
// segundos até o GET /vehicle-metadata/images terminar (useVehicleImages só
// habilita depois que a lista de veículos carrega) — parece "sumiu a foto".
// Guardando a última resposta conhecida, o card já nasce com a foto certa
// e revalida em segundo plano (initialDataUpdatedAt: 0 abaixo).
const VEHICLE_IMAGES_CACHE_KEY = "gpsdrive.vehicleImages.cache.v1";

function readVehicleImagesCache(): Record<string, VehicleImageUrls> {
  try {
    const raw = localStorage.getItem(VEHICLE_IMAGES_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeVehicleImagesCache(entries: Record<string, VehicleImageUrls>) {
  try {
    const merged = readVehicleImagesCache();
    for (const [deviceId, urls] of Object.entries(entries)) merged[deviceId] = urls;
    localStorage.setItem(VEHICLE_IMAGES_CACHE_KEY, JSON.stringify(merged));
  } catch {
    // localStorage indisponível (aba anônima/quota) — cache é só otimização.
  }
}

export const queryKeys = {
  vehicles: ["vehicles"] as const,
  vehicleTrash: ["vehicle-trash"] as const,
  userDeletions: ["user-deletions"] as const,
  vehicle: (id: string) => ["vehicles", id] as const,
  positions: (vehicleId?: string) => ["positions", vehicleId ?? "all"] as const,
  alerts: ["alerts"] as const,
  alertRules: (deviceId: number) => ["alert-rules", deviceId] as const,
  geofenceAlertRules: ["alert-rules", "geofence-summary"] as const,
  quickToggleRules: (deviceId: number) => ["alert-rules", "quick-toggle", deviceId] as const,
  alertRuleEvents: (deviceId?: number) => ["alert-rule-events", deviceId ?? "all"] as const,
  users: ["users"] as const,
  user: (id: number) => ["users", id] as const,
  usersSummary: (traccarUserIds: number[]) => ["users-summary", traccarUserIds.join(",")] as const,
  linkableUsers: ["linkable-users"] as const,
  devicePermissions: (userId: number) => ["device-permissions", userId] as const,
  vehicleUsers: (deviceId: number) => ["vehicle-users", deviceId] as const,
  vehicleMetadata: (deviceId: string | number) => ["vehicle-metadata", String(deviceId)] as const,
  userAvatar: (traccarUserId: number) => ["user-avatar", traccarUserId] as const,
  gpsDriveRoles: ["gpsdrive-roles"] as const,
  gpsDriveUserIdentity: (traccarUserId: number) =>
    ["gpsdrive-user-identity", traccarUserId] as const,
  userPermissions: (userId: string) => ["user-permissions", userId] as const,
  clientProfile: (userId: string) => ["client-profile", userId] as const,
  clientContract: (userId: string) => ["client-contract", userId] as const,
  clientDocuments: (userId: string) => ["client-documents", userId] as const,
  subUsersByCreator: (userId: string) => ["sub-users-by-creator", userId] as const,
  activityLog: (userId: string, type: string, offset: number) =>
    ["activity-log", userId, type, offset] as const,
  userSessions: (userId: string) => ["user-sessions", userId] as const,
  asaasIntegration: ["asaas-integration"] as const,
  whatsappIntegration: ["whatsapp-integration"] as const,
  communicationFailure: ["communication-failure"] as const,
  myPermissions: ["my-permissions"] as const,
  adminBranding: ["admin-branding"] as const,
  publicLoginBranding: ["public-login-branding"] as const,
  quickAccessCatalog: ["quick-access-catalog"] as const,
  myQuickAccess: ["my-quick-access"] as const,
  contractTemplates: ["contract-templates"] as const,
  contractSignatories: ["contract-signatories"] as const,
  contractCompany: ["contract-company"] as const,
  welcomeMessage: ["welcome-message"] as const,
  plans: ["plans"] as const,
  gt06Commands: ["gt06-commands"] as const,
  assistanceServiceTypes: ["assistance-service-types"] as const,
  assistanceServiceGroups: ["assistance-service-groups"] as const,
  assistanceClientSummary: (traccarUserId: number) =>
    ["assistance-client-summary", traccarUserId] as const,
  assistanceUsage: (traccarUserId: number, deviceId: number, serviceTypeId: string) =>
    ["assistance-usage", traccarUserId, deviceId, serviceTypeId] as const,
  myAssistanceRequests: ["my-assistance-requests"] as const,
  invoices: (userId: string, offset: number) => ["invoices", userId, offset] as const,
  myBillingStatus: ["billing-status", "me"] as const,
  vehicleImages: (deviceIds: string[]) =>
    ["vehicle-images", [...deviceIds].sort().join(",")] as const,
  vehicleMetadataSummaries: (deviceIds: string[]) =>
    ["vehicle-metadata-summaries", [...deviceIds].sort().join(",")] as const,
  vehicleResellerCompanyMap: (deviceIds: string[]) =>
    ["vehicle-reseller-company-map", [...deviceIds].sort().join(",")] as const,
  myCompany: ["my-company"] as const,
  commands: (vehicleId?: string) => ["commands", vehicleId ?? "all"] as const,
  geofences: ["geofences"] as const,
  geofence: (id: number) => ["geofences", id] as const,
  geofenceDevices: (geofenceId: number) => ["geofence-devices", geofenceId] as const,
  notificationTypes: ["notification-types"] as const,
  deviceNotifications: (deviceId: number) => ["notifications", "device", deviceId] as const,
  trips: (vehicleId: string, from: string, to: string) => ["trips", vehicleId, from, to] as const,
  route: (vehicleId: string, from: string, to: string) => ["route", vehicleId, from, to] as const,
  events: (vehicleId: string, from: string, to: string) =>
    ["vehicle-events", vehicleId, from, to] as const,
  routePositions: (vehicleId: string, from: string, to: string) =>
    ["route-positions", vehicleId, from, to] as const,
  summary: (vehicleId: string, from: string, to: string) =>
    ["route-summary", vehicleId, from, to] as const,
  stops: (vehicleId: string, from: string, to: string) =>
    ["route-stops", vehicleId, from, to] as const,
  historyDeletions: (traccarDeviceId: number) => ["history-deletions", traccarDeviceId] as const,

  // Tela de Relatórios — multi-veículo (deviceIds serializado, já que arrays
  // não são chave estável por identidade de referência no React Query).
  reportKey: (kind: string, deviceIds: string[], from: string, to: string) =>
    [kind, deviceIds.join(","), from, to] as const,
};

export function useVehicles() {
  // Reforça re-render (não refetch) quando overrides locais mudam — usado só
  // pra fazer `select` rodar de novo em cima dos MESMOS dados já em cache. Achado
  // 2026-09-02, load test de 5000 veículos: antes, `tick` fazia parte da
  // queryKey (["vehicles", tick]) — cada mudança criava uma entrada NOVA no
  // cache do React Query (refetch completo de devices+positions da frota
  // inteira, e a antiga ficava presa no cache até o gcTime expirar). Como
  // useRealtimeSync (montado globalmente em __root.tsx) varre TODA entrada
  // com prefixo ["vehicles"] a cada mensagem de WebSocket e faz um
  // `.map()` sobre a frota inteira em cada uma, entradas acumuladas
  // multiplicavam esse custo — mapa ficando cada vez mais lento ao longo da
  // sessão conforme mais entradas se acumulavam. `select` já é uma função
  // nova a cada render (arrow function inline), então o próprio re-render
  // disparado por setTick já é suficiente pra recalcular mergeVehicleData
  // sobre o dado em cache — não precisa (e não deve) criar uma query nova.
  const [, setTick] = useState(0);
  useEffect(() => {
    const onChange = () => setTick((t) => t + 1);
    window.addEventListener("wp-vehicle-overrides", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("wp-vehicle-overrides", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  return useQuery({
    queryKey: queryKeys.vehicles,
    queryFn: () => vehiclesService.list(),
    select: (list) => mergeVehicleData(list),
    refetchInterval: 15_000,
    retry: false,
    // Pedido 2026-09-04: F5/login não deve mostrar "carregando"/"0
    // veículos" — nasce com a última frota conhecida (localStorage, ver
    // vehicles.service.ts#readVehicleListCache) e revalida contra o
    // servidor imediatamente por trás (initialDataUpdatedAt: 0 marca esse
    // dado como já staleado, então o fetch real dispara na hora mesmo
    // assim, só não bloqueia a primeira renderização esperando ele).
    initialData: () => readVehicleListCache(),
    initialDataUpdatedAt: 0,
  });
}

export function useVehicle(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicle(id ?? ""),
    queryFn: () => vehiclesService.get(id as string),
    enabled: !!id,
    retry: false,
  });
}

export function usePositions(vehicleId?: string) {
  return useQuery({
    queryKey: queryKeys.positions(vehicleId),
    queryFn: () => (vehicleId ? positionsService.byVehicle(vehicleId) : positionsService.latest()),
    retry: false,
  });
}

export function useAlerts() {
  return useQuery({
    queryKey: queryKeys.alerts,
    queryFn: () => alertsService.list(),
    refetchInterval: 20_000,
    retry: false,
  });
}

// Motor de alertas real (Fase 3, GPS Drive Backend) — regras configuradas
// por veículo (aba "Alertas" de Editar Dispositivo). Distinto de
// useAlerts()/alertsService acima (mock não relacionado).
export function useAlertRules(deviceId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.alertRules(deviceId ?? 0),
    queryFn: () => alertRulesService.listRules(deviceId as number),
    enabled: !!deviceId,
    retry: false,
  });
}

export function useCreateAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAlertRuleInput) => alertRulesService.createRule(input),
    onSuccess: (_rule, input) =>
      qc.invalidateQueries({ queryKey: queryKeys.alertRules(input.deviceId) }),
  });
}

// "Criar alerta para esta cerca" (drawer de Cercas Virtuais) — cria uma
// AlertRule por veículo selecionado de uma vez (POST .../geofence-batch).
// Invalida por deviceId (mesma chave que useAlertRules usa) para cada
// veículo vinculado, já que a aba "Alertas" de Editar Dispositivo lê essa
// mesma lista.
export function useCreateGeofenceAlertRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGeofenceAlertRuleBatchInput) =>
      alertRulesService.createGeofenceRules(input),
    onSuccess: (_rules, input) => {
      for (const deviceId of input.deviceIds) {
        qc.invalidateQueries({ queryKey: queryKeys.alertRules(deviceId) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.geofenceAlertRules });
    },
  });
}

// Todas as regras de geocerca da empresa — usado pela lista de Cercas
// Virtuais (badge/filtro "Com bloqueio" + pré-preencher o painel de
// alerta ao reabrir uma cerca que já tem alerta configurado).
export function useGeofenceAlertRules() {
  return useQuery({
    queryKey: queryKeys.geofenceAlertRules,
    queryFn: () => alertRulesService.listGeofenceRules(),
    retry: false,
  });
}

export function useUpdateAlertRule(deviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateAlertRuleInput }) =>
      alertRulesService.updateRule(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.alertRules(deviceId) }),
  });
}

export function useDeleteAlertRule(deviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => alertRulesService.deleteRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.alertRules(deviceId) }),
  });
}

// Tela "Notificações" do app do cliente (ALERT_CATALOG#alertRuleType,
// MobileNotificationsPanel/TabAlertas/VehicleAlertsPanel) — GET/PUT
// próprios (.../rules/quick-toggle), sem exigir a permissão granular
// "notifications" que useAlertRules acima exige (essa tela é pro cliente
// comum configurar o próprio veículo, não só Administrator/Manager).
export function useQuickToggleRules(deviceId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.quickToggleRules(deviceId ?? 0),
    queryFn: () => alertRulesService.listQuickToggleRules(deviceId as number),
    enabled: !!deviceId,
    retry: false,
  });
}

export function useQuickToggleRule(deviceId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { type: AlertType; enabled: boolean; config?: AlertRuleConfig; actionKind?: "notify" | "block" }) =>
      alertRulesService.quickToggleRule({ deviceId: deviceId as number, ...payload }),
    onSuccess: () => {
      if (deviceId) qc.invalidateQueries({ queryKey: queryKeys.quickToggleRules(deviceId) });
    },
  });
}

// Sino de notificações — sem deviceId, lista a frota inteira da empresa.
// Poll leve (o motor no backend já roda a cada 30s; refetch mais rápido
// que isso só gastaria requisição à toa).
export function useAlertRuleEvents(deviceId?: number) {
  return useQuery({
    queryKey: queryKeys.alertRuleEvents(deviceId),
    queryFn: () => alertRulesService.listEvents(deviceId),
    refetchInterval: 30_000,
    retry: false,
  });
}

// Aba "Alertas" de Editar dispositivo — pedido 2026-10-09 ("demora para
// aparecer os alertas"): o backend responde em poucos ms, a espera era a
// aba só começar a buscar regras/histórico no clique. Agora o diálogo
// dispara as duas consultas assim que abre. O histórico é paginado e
// filtrado no servidor (/events/page) — com /events (teto de 200) a aba
// não alcançava o histórico inteiro de veículos com milhares de eventos.
export const VEHICLE_ALERT_EVENTS_PAGE_SIZE = 10;

export function useAlertEventsPage(params: AlertEventsPageParams) {
  return useQuery({
    queryKey: ["alert-rule-events", "page", params] as const,
    queryFn: () => alertRulesService.listEventsPage(params),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    retry: false,
  });
}

export function prefetchVehicleAlerts(qc: ReturnType<typeof useQueryClient>, deviceId: number) {
  const firstPage: AlertEventsPageParams = { deviceId, page: 1, pageSize: VEHICLE_ALERT_EVENTS_PAGE_SIZE };
  void qc.prefetchQuery({
    queryKey: queryKeys.alertRules(deviceId),
    queryFn: () => alertRulesService.listRules(deviceId),
    retry: false,
  });
  void qc.prefetchQuery({
    queryKey: ["alert-rule-events", "page", firstPage] as const,
    queryFn: () => alertRulesService.listEventsPage(firstPage),
    retry: false,
  });
}

export function useMarkAlertRuleEventRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => alertRulesService.markEventRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-rule-events"] }),
  });
}

// `enabled` (default true, pedido 2026-09-14) — gate opcional pro picker
// de cliente do formulário de Agendamento (agenda-appointment-form-dialog.tsx),
// que só precisa buscar a lista quando o Dialog está aberto, mesmo padrão
// de useCompanyFleet(open) já usado ali. Nunca muda o comportamento dos
// chamadores existentes (routes/users.tsx chama sem argumento).
export function useUsers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => usersService.list(),
    enabled,
    // Nunca mostra a conta de serviço fixa do Traccar (TRACCAR_USER) na
    // tela de Usuários — pedido 2026-09-01, ver lib/protected-accounts.ts.
    select: (list) => list.filter((u) => !isProtectedServiceAccount(u.email)),
    retry: false,
    // Pedido 2026-09-04 (mesmo tratamento da frota, ver
    // gpsdrive_vehicle_list_snapshot_cache_2026-09-04): nasce com a última
    // lista conhecida (localStorage) em vez de "carregando", e revalida
    // contra o servidor na hora por trás (initialDataUpdatedAt: 0). Criar/
    // editar/excluir usuário continua batendo instantâneo — invalidateQueries
    // nos mutations acima dispara um refetch de verdade, que também
    // reescreve este snapshot com o dado já atualizado.
    initialData: () => readUsersListCache(),
    initialDataUpdatedAt: 0,
  });
}

// Resumo em lote da tabela de Usuários (avatar, Grupo, CPF/CNPJ, celular,
// veículos vinculados, recorrência) — UMA chamada pra tabela inteira, no
// lugar dos 5-7 requests que cada UserRow disparava sozinha antes (ver
// diagnóstico de lentidão 2026-09-11 e gpsdrive-users-summary.service.ts).
// `traccarUserIds` vem de useUsers() (já carregado) — string joined como
// parte da queryKey para o React Query cachear/revalidar por conjunto de
// ids, mas o array em si só entra na query quando `enabled` (nunca busca
// resumo de uma lista vazia/ainda carregando). initialData filtra o
// snapshot salvo pelos ids atuais — mesmo padrão de useUsers() acima,
// F5 já nasce com avatar/documento/veículos reais em vez de skeleton.
export function useUsersSummary(traccarUserIds: number[], enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.usersSummary(traccarUserIds),
    queryFn: () => gpsDriveUsersSummaryService.summary(traccarUserIds),
    enabled: enabled && traccarUserIds.length > 0,
    retry: 2,
    initialData: () => {
      if (traccarUserIds.length === 0) return undefined;
      const cache = readUsersSummaryCache();
      const rows = traccarUserIds.map((id) => cache[id]).filter((r): r is UserRowSummary => Boolean(r));
      return rows.length === traccarUserIds.length ? rows : undefined;
    },
    initialDataUpdatedAt: 0,
  });
}

// Fonte real dos dois pickers de vínculo usuário↔veículo (isolamento
// multiempresa 2026-08-16, ver relatório da etapa) — GET /api/v1/users do
// GPS Drive Backend, nunca GET /api/users direto no Traccar. Deliberadamente
// separado de useUsers() acima: aquele alimenta a tela de administração
// "Usuários" (CRUD completo, sem relação com empresa), este alimenta só
// PermissionLinkPicker em vehicle-access-dialog.tsx e routes/index.tsx.
export function useLinkableUsers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.linkableUsers,
    queryFn: () => gpsDriveUsersService.listLinkable(),
    enabled,
    retry: false,
  });
}

export function useUser(id: number | undefined) {
  return useQuery({
    queryKey: queryKeys.user(id ?? 0),
    queryFn: () => usersService.get(id as number),
    enabled: !!id,
    retry: false,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<import("@/types").User>) => usersService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<import("@/types").User> }) =>
      usersService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    // Recebe o e-mail junto (não só o id) pra poder barrar a exclusão da
    // conta de serviço do Traccar aqui, ANTES da chamada sair pro Traccar
    // — última linha de defesa server-independente (ver
    // lib/protected-accounts.ts; a tela já nem lista essa conta, mas esta
    // função pode ganhar outros chamadores no futuro).
    //
    // `reason` (pedido 2026-09-30): o motivo é gravado no GPS Drive Backend
    // ANTES de excluir (depois não existe mais de onde copiar nome/login);
    // se a exclusão em si falhar, o registro é desfeito pra não sobrar um
    // "excluído" que continua existindo.
    mutationFn: async ({ id, email, reason }: { id: number; email: string; reason: string }) => {
      if (isProtectedServiceAccount(email)) {
        throw new Error("Esta conta é usada internamente pelo sistema e não pode ser excluída.");
      }
      const record = await gpsDriveUserDeletionsService.record(id, reason);
      try {
        await usersService.remove(id);
      } catch (err) {
        await gpsDriveUserDeletionsService.cancel(record.id).catch(() => {});
        throw err;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users });
      qc.invalidateQueries({ queryKey: queryKeys.userDeletions });
    },
  });
}

// Usuários excluídos + motivo (pedido 2026-09-30) — ver
// gpsdrive-user-deletions.service.ts.
export function useUserDeletions(enabled = true) {
  return useQuery({
    queryKey: queryKeys.userDeletions,
    queryFn: () => gpsDriveUserDeletionsService.list(),
    enabled,
    retry: false,
  });
}

// Veículos já vinculados a um usuário (tc_user_device, via GPS Drive
// Backend → Traccar) — devolve o device bruto (não só o id) porque um
// veículo pode estar vinculado sem nunca ter sido "reivindicado"
// (vehicle_metadata) pela empresa; nesse caso GET /api/v1/vehicles
// (useCompanyFleet) não o lista, e users.tsx precisa dos dados do device
// (nome/placa) pra ainda assim mostrá-lo na aba "Veículos" — ver
// diagnóstico 2026-08-26 (frota da empresa some veículo vinculado sem
// vehicle_metadata).
export function useDevicePermissions(userId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.devicePermissions(userId ?? 0),
    queryFn: () => vehiclePermissionsService.vehiclesForUser(userId as number),
    enabled: !!userId && enabled,
    retry: false,
  });
}

// Frota da empresa (GET /api/v1/vehicles, Fase 1 — Administrator/Manager)
// — só veículos com vehicle_metadata (ownership) já atribuído a esta
// empresa; um veículo vinculado (tc_user_device) mas nunca "reivindicado"
// não aparece aqui, só em useDevicePermissions (ver comentário acima).
// Nunca useVehicles()/GET /api/devices, que reflete a sessão de quem está
// logado, não a empresa do usuário sendo editado (ver diagnóstico
// 2026-08-26). 403 (Operator/Viewer abrindo o diálogo) vira lista vazia,
// nunca um crash — mesmo padrão de queryFn falhando com retry:false.
export function useCompanyFleet(enabled: boolean) {
  return useQuery({
    queryKey: ["company-fleet"] as const,
    queryFn: () => vehiclePermissionsService.vehiclesForCompany(),
    enabled,
    retry: false,
  });
}

// Posição real de um veículo específico da empresa — usado só pela Âncora
// (aba VEÍCULOS de Editar Usuário, ver vehicle-anchor-dialog.tsx), que
// nessa tela só tem um TraccarDeviceRef sem lat/lng à mão (a lista ali não
// usa useVehicles() de propósito, ver comentário em useCompanyFleet acima).
// `enabled` fica false até o operador realmente clicar no ícone de âncora
// de uma linha — nunca busca a posição das dezenas/centenas de veículos
// vinculados de uma vez.
export function useVehicleCompanyPosition(deviceId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["vehicle-company-position", deviceId ?? 0] as const,
    queryFn: () => vehiclePermissionsService.vehiclePosition(deviceId as number),
    enabled: enabled && !!deviceId,
    retry: false,
  });
}

// Envia a lista COMPLETA de veículos desejada — o backend calcula o diff
// contra o estado atual no Traccar e aplica só a diferença (nunca desfaz e
// refaz vínculos que já estavam corretos), ver
// gpsdrive-backend/src/permissions/permissions.repository.ts#syncUserDevices.
export function useSyncUserDevicePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, deviceIds }: { userId: number; deviceIds: string[] }) => {
      await vehiclePermissionsService.syncUserVehicles(userId, deviceIds.map(Number));
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.users });
      qc.invalidateQueries({ queryKey: queryKeys.linkableUsers });
      qc.invalidateQueries({ queryKey: queryKeys.vehicles });
      qc.invalidateQueries({ queryKey: queryKeys.devicePermissions(vars.userId) });
      qc.invalidateQueries({ queryKey: ["vehicle-users"] });
    },
  });
}

// Lado simétrico: usuários já vinculados a um veículo (tela Veículos →
// Vincular usuário). Mesmo backend/relação de useDevicePermissions acima,
// só o sentido da consulta muda.
export function useVehicleUsers(deviceId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicleUsers(deviceId ?? 0),
    queryFn: () => vehiclePermissionsService.usersForVehicle(deviceId as number),
    enabled: !!deviceId,
    retry: false,
  });
}

export function useSyncVehicleUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, traccarUserIds }: { deviceId: number; traccarUserIds: number[] }) =>
      vehiclePermissionsService.syncVehicleUsers(deviceId, traccarUserIds),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.vehicleUsers(vars.deviceId) });
      qc.invalidateQueries({ queryKey: queryKeys.linkableUsers });
      qc.invalidateQueries({ queryKey: ["device-permissions"] });
    },
  });
}

// Cadastro real do veículo (GPS Drive Backend) — marca/modelo/ano/
// rastreador/instalação/imagens. Nunca confundir com useVehicle (Traccar).
export function useVehicleMetadata(deviceId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicleMetadata(deviceId ?? ""),
    queryFn: () => vehicleMetadataService.get(deviceId as string),
    enabled: !!deviceId,
    retry: false,
  });
}

// Avatar do usuário (GPS Drive Backend) — sem relação com empresa, ver
// gpsdrive-users.service.ts. traccarUserId undefined = dialog fechado
// ainda sem usuário resolvido.
export function useUserAvatar(traccarUserId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.userAvatar(traccarUserId ?? 0),
    queryFn: () => gpsDriveUsersService.getAvatar(traccarUserId as number),
    enabled: !!traccarUserId && enabled,
    retry: false,
  });
}

// Editar a própria foto (app do cliente, aba Perfil) — mesmo par de
// endpoints que o dialog admin "Editar Usuário" já usa (ver
// gpsdrive-users.service.ts), só que exposto como mutation reaproveitável
// em vez de chamada direta ao service.
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ traccarUserId, file }: { traccarUserId: number; file: File }) =>
      gpsDriveUsersService.uploadAvatar(traccarUserId, file),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.userAvatar(vars.traccarUserId) }),
  });
}

export function useRemoveAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (traccarUserId: number) => gpsDriveUsersService.removeAvatar(traccarUserId),
    onSuccess: (_d, traccarUserId) =>
      qc.invalidateQueries({ queryKey: queryKeys.userAvatar(traccarUserId) }),
  });
}

// Papéis e matriz de permissões (GPS Drive Backend, por papel — ver
// gpsdrive-roles.service.ts). `enabled` deixa o chamador controlar quando
// buscar (ex.: só com o dialog aberto).
export function useGpsDriveRoles(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.gpsDriveRoles,
    queryFn: () => gpsDriveRolesService.list(),
    enabled,
    // Único `retry` != false do arquivo, de propósito: esta lista alimenta
    // o select "Grupo" inteiro (Administrator/Manager/Operator/Viewer +
    // Grupos criados) — uma falha transiente (ex.: 401 no meio da corrida
    // de renovação de token, ver auth.service.ts#REFRESH_REUSE_GRACE_MS)
    // deixava esse select vazio e travado pro resto da sessão, já que
    // `retry: false` nunca tenta de novo sozinho (bug real 2026-08-27:
    // usuário via "não mostra nem permite selecionar as opções
    // disponíveis" sem nenhum jeito de se recuperar sem recarregar a
    // página). 2 tentativas é suficiente pra sobreviver a esse tipo de
    // corrida pontual sem martelar o servidor num erro persistente.
    retry: 2,
  });
}

// Resolve o id interno (UUID) do GPS Drive a partir do traccarUserId do
// usuário sendo editado — é assim que a aba PERMISSÕES descobre qual
// userId usar em GET/PUT /users/:userId/permissions (provisiona na hora
// se for a primeira vez, ver backend). `enabled` deixa o chamador
// controlar quando resolver (ex.: só com o dialog de edição aberto).
export function useResolveUserByTraccarId(traccarUserId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.gpsDriveUserIdentity(traccarUserId ?? 0),
    queryFn: () => gpsDriveUsersService.resolveByTraccarId(traccarUserId as number),
    enabled: enabled && !!traccarUserId,
    // retry:false aqui já causou o mesmo bug real do useGpsDriveRoles acima
    // (2026-08-27): dispara logo que o dialog "vira" persisted/muda de
    // usuário — exatamente o tipo de corrida pontual (401 no meio da
    // renovação de token, etc.) que travava a aba de Permissões/Cliente
    // pra sempre em "Carregando…" sem nenhum jeito de se recuperar sem
    // fechar e reabrir o dialog.
    retry: 2,
  });
}

// Permissões granulares REAIS do usuário (GPS Drive Backend, por USUÁRIO
// — ver gpsdrive-user-permissions.service.ts).
export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userPermissions(userId ?? ""),
    queryFn: () => gpsDriveUserPermissionsService.get(userId as string),
    enabled: !!userId,
    // Ver comentário em useResolveUserByTraccarId: mesma corrida pontual.
    retry: 2,
  });
}

export function useUpdateUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, rows }: { userId: string; rows: PermissionCategoryState[] }) =>
      gpsDriveUserPermissionsService.replace(userId, rows),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.userPermissions(vars.userId) }),
  });
}

// Ação explícita "Restaurar permissões padrão do grupo" — nunca disparada
// automaticamente ao trocar o Grupo do usuário (ver useChangeUserRole).
export function useRestoreDefaultUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => gpsDriveUserPermissionsService.restoreDefaults(userId),
    onSuccess: (_data, userId) =>
      qc.invalidateQueries({ queryKey: queryKeys.userPermissions(userId) }),
  });
}

// Dados de Cliente/Financeiro (aba CLIENTE, GPS Drive Backend — ver
// gpsdrive-client-profile.service.ts). `data` vem `null` se a aba nunca
// foi salva pra este usuário ainda.
export function useClientProfile(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.clientProfile(userId ?? ""),
    queryFn: () => gpsDriveClientProfileService.get(userId as string),
    enabled: !!userId,
    // Ver comentário em useResolveUserByTraccarId: mesma corrida pontual.
    retry: 2,
  });
}

export function useUpdateClientProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateGpsDriveClientProfileInput }) =>
      gpsDriveClientProfileService.replace(userId, data),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.clientProfile(vars.userId) }),
  });
}

// Aba CONTRATO (Editar Usuário) — modelo de contrato + signatário
// selecionados, valores de contrato/recorrência. `data` vem `null` se a
// aba nunca foi salva pra este usuário ainda.
// Contratos assinados anteriores (2026-10-15).
export function useClientContractVersions(userId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.clientContract(userId ?? ""), "versions"],
    queryFn: () => gpsDriveClientContractService.versions(userId as string),
    enabled: !!userId,
    retry: false,
  });
}

export function useClientContract(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.clientContract(userId ?? ""),
    queryFn: () => gpsDriveClientContractService.get(userId as string),
    enabled: !!userId,
    retry: false,
  });
}

export function useUpdateClientContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateClientContractInput }) =>
      gpsDriveClientContractService.replace(userId, data),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.clientContract(vars.userId) }),
  });
}

// Excluir contrato não assinado (2026-10-16). Se havia um assinado antes
// ("Novo contrato"), ele volta a ser o atual — por isso grava a resposta
// e recarrega também os "Contratos anteriores".
export function useDeleteClientContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => gpsDriveClientContractService.remove(userId),
    onSuccess: (data, userId) => {
      qc.setQueryData(queryKeys.clientContract(userId), data ?? null);
      return qc.invalidateQueries({ queryKey: queryKeys.clientContract(userId) });
    },
  });
}

export function useDeleteClientContractVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, versionId }: { userId: string; versionId: string }) =>
      gpsDriveClientContractService.removeVersion(userId, versionId),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: queryKeys.clientContract(vars.userId) }),
  });
}

// "Criar contrato" com o atual já assinado: guarda o assinado como anterior
// e reabre como versão +1 (ver ClientContractsService#startNewVersion).
export function useStartNewClientContractVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => gpsDriveClientContractService.startNewVersion(userId),
    onSuccess: (data, userId) => {
      qc.setQueryData(queryKeys.clientContract(userId), data);
      return qc.invalidateQueries({ queryKey: queryKeys.clientContract(userId) });
    },
  });
}

// Botão "Gerar link de assinatura" — token único pra
// /contrato/assinar/:token (autoatendimento do cliente, sem login).
export function useGenerateContractSigningLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => gpsDriveClientContractService.generateSigningLink(userId),
    onSuccess: (_data, userId) => qc.invalidateQueries({ queryKey: queryKeys.clientContract(userId) }),
  });
}

// Botão "Enviar por WhatsApp" (link de assinatura) — só disponível quando a
// integração está conectada (ver useWhatsappIntegration).
export function useSendSigningLinkByWhatsapp() {
  return useMutation({
    mutationFn: ({ userId, url }: { userId: string; url: string }) =>
      gpsDriveClientContractService.sendSigningLinkByWhatsapp(userId, url),
  });
}

export function useClientDocuments(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.clientDocuments(userId ?? ""),
    queryFn: () => gpsDriveClientDocumentsService.list(userId as string),
    enabled: !!userId,
    retry: false,
  });
}

// Sub-usuários (pedido 2026-09-10/11). useSubUsersByCreator alimenta a aba
// USUÁRIOS em Editar Usuário (web) — lista quem um dado Cliente já criou.
// useMySubUsers/useCreateSubUser/useUpdateSubUser/useDeleteSubUser são
// usados pelo popup "Criar usuário" no Perfil do PWA (o titular gerencia
// os próprios sub-usuários ali). useCompanySubUserTraccarIds alimenta o
// filtro que esconde sub-usuários da lista PRINCIPAL de Usuários.
export function useSubUsersByCreator(creatorUserId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.subUsersByCreator(creatorUserId ?? ""),
    queryFn: () => gpsDriveSubUsersService.listByCreator(creatorUserId as string),
    enabled: !!creatorUserId,
    retry: false,
  });
}

export function useMySubUsers(enabled: boolean) {
  return useQuery({
    queryKey: ["sub-users-mine"],
    queryFn: () => gpsDriveSubUsersService.listMine(),
    enabled,
    retry: false,
  });
}

export function useCompanySubUserTraccarIds(enabled: boolean) {
  return useQuery({
    queryKey: ["sub-users-company-traccar-ids"],
    queryFn: () => gpsDriveSubUsersService.companyTraccarIds(),
    enabled,
    retry: false,
    staleTime: 60 * 1000,
    // Mesmo padrão do useUsers() (ver gpsdrive_users_list_snapshot_cache):
    // nasce com o último resultado conhecido pra não deixar a lista
    // principal de Usuários mostrar sub-usuários "crus" por 1-2s até essa
    // query real resolver. initialDataUpdatedAt: 0 força o refetch real
    // mesmo assim (isLoading vira false, mas o fetch continua acontecendo).
    initialData: () => readCompanySubUserTraccarIdsCache(),
    initialDataUpdatedAt: 0,
  });
}

export function useSubUserDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["sub-user-detail", id ?? ""],
    queryFn: () => gpsDriveSubUsersService.getDetail(id as string),
    enabled: !!id,
    retry: false,
  });
}

function invalidateSubUserQueries(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["sub-users-by-creator"] });
  void qc.invalidateQueries({ queryKey: ["sub-users-mine"] });
  void qc.invalidateQueries({ queryKey: ["sub-users-company-traccar-ids"] });
}

export function useCreateSubUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubUserInput) => gpsDriveSubUsersService.create(input),
    onSuccess: () => invalidateSubUserQueries(qc),
  });
}

export function useUpdateSubUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSubUserInput }) =>
      gpsDriveSubUsersService.update(id, input),
    onSuccess: (_data, { id }) => {
      invalidateSubUserQueries(qc);
      void qc.invalidateQueries({ queryKey: ["sub-user-detail", id] });
    },
  });
}

export function useDeleteSubUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveSubUsersService.remove(id),
    onSuccess: () => invalidateSubUserQueries(qc),
  });
}

export function useUploadClientDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      file,
      category,
    }: {
      userId: string;
      file: File;
      category: ClientDocumentCategory;
    }) => gpsDriveClientDocumentsService.upload(userId, file, category),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.clientDocuments(vars.userId) }),
  });
}

export function useDeleteClientDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, documentId }: { userId: string; documentId: string }) =>
      gpsDriveClientDocumentsService.remove(userId, documentId),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.clientDocuments(vars.userId) }),
  });
}

// Aba LOGS (Editar Usuário) — histórico de atividade real do usuário
// (login, erros, comandos de veículo, geocerca, push, notificações). Só
// busca com a aba aberta (`enabled`, controlado pelo caller) e paginado
// por offset — o componente acumula páginas para o botão "Carregar mais".
export function useUserActivityLog(
  userId: string | undefined,
  filters: ActivityLogFilters,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.activityLog(userId ?? "", filters.type ?? "all", filters.offset ?? 0),
    queryFn: () => activityLogService.getUserActivityLog(userId as string, filters),
    enabled: enabled && !!userId,
    retry: false,
  });
}

// Aba DISPOSITIVOS CONECTADOS (Editar Usuário) — sessões reais
// (refresh_tokens ativos, ver gpsdrive-user-sessions.service.ts).
export function useUserSessions(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userSessions(userId ?? ""),
    queryFn: () => gpsDriveUserSessionsService.list(userId as string),
    enabled: !!userId,
    retry: false,
  });
}

export function useRevokeUserSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, sessionId }: { userId: string; sessionId: string }) =>
      gpsDriveUserSessionsService.revoke(userId, sessionId),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.userSessions(vars.userId) }),
  });
}

// Tokens de push nativo (app Android) — mesma aba, canal separado das
// sessões web acima (pedido 2026-09-11: "tem que ter o token gerado...
// quando o usuário acessou o app").
export function useUserFcmTokens(userId: string | undefined) {
  return useQuery({
    queryKey: ["user-fcm-tokens", userId ?? ""],
    queryFn: () => gpsDriveUserFcmTokensService.list(userId as string),
    enabled: !!userId,
    retry: false,
  });
}

export function useRevokeUserFcmToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, tokenId }: { userId: string; tokenId: string }) =>
      gpsDriveUserFcmTokensService.revoke(userId, tokenId),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["user-fcm-tokens", vars.userId] }),
  });
}

// Integração com o Asaas (tela Integrações) — status atual da empresa do
// usuário logado (administrador-only, ver gpsdrive-asaas-integration.service.ts).
export function useAsaasIntegration() {
  return useQuery({
    queryKey: queryKeys.asaasIntegration,
    queryFn: () => gpsDriveAsaasIntegrationService.get(),
    retry: false,
  });
}

export function useUpdateAsaasIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAsaasIntegrationInput) =>
      gpsDriveAsaasIntegrationService.update(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.asaasIntegration }),
  });
}

// Integração com o WhatsApp via uazapi (tela Integrações) — conta
// centralizada, sem chave por empresa (ver
// gpsdrive-whatsapp-integration.service.ts). refetchInterval curto e
// condicional: o dialog de conexão precisa saber quando o QR foi
// escaneado sem que o usuário precise recarregar a página.
export function useWhatsappIntegration(opts?: { pollWhileConnecting?: boolean }) {
  return useQuery({
    queryKey: queryKeys.whatsappIntegration,
    queryFn: () => gpsDriveWhatsappIntegrationService.getStatus(),
    retry: false,
    refetchInterval: opts?.pollWhileConnecting ? 3000 : false,
  });
}

export function useConnectWhatsappIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gpsDriveWhatsappIntegrationService.connect(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.whatsappIntegration }),
  });
}

export function useDisconnectWhatsappIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gpsDriveWhatsappIntegrationService.disconnect(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.whatsappIntegration }),
  });
}

// Falha de Comunicação (Administração > Comunicação) — limite único por
// empresa, ver gpsdrive-communication-failure.service.ts.
export function useCommunicationFailureSettings() {
  return useQuery({
    queryKey: queryKeys.communicationFailure,
    queryFn: () => gpsDriveCommunicationFailureService.get(),
    retry: false,
  });
}

export function useUpdateCommunicationFailureSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCommunicationFailureInput) =>
      gpsDriveCommunicationFailureService.update(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.communicationFailure }),
  });
}

export function useTestSendCommunicationFailure() {
  return useMutation({
    mutationFn: (input: TestSendCommunicationFailureInput) =>
      gpsDriveCommunicationFailureService.testSend(input),
  });
}

// Permissões do PRÓPRIO usuário logado — usado por usePermission (ver
// hooks/use-permission.ts) para decidir o que mostrar (ex.: item
// "Administração" na sidebar). staleTime generoso: é lido em toda página
// só para gating, não precisa refletir uma mudança de permissão em tempo
// real (a próxima navegação/F5 já pega o valor atualizado).
export function useMyPermissions() {
  return useQuery({
    queryKey: queryKeys.myPermissions,
    queryFn: () => gpsDriveUserPermissionsService.getMine(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    // Bug real 2026-09-01: em todo login/impersonate, um monte de
    // componente (VehicleRow, users.tsx, AdminAccessGate...) já monta e
    // chama usePermission() no MESMO instante em que o Traccar autentica
    // — mas o access token do GPS Drive Backend só existe alguns
    // milissegundos depois, quando login()/recoverSession() (ver
    // auth-context.tsx) resolve. Sem este `enabled`, a PRIMEIRA busca
    // sempre saía com Authorization vazio, tomava 401, e o retry-once de
    // fetchWithAuth disparava um refresh CONCORRENTE com o próprio login()
    // em andamento — se esse refresh paralelo falhasse por qualquer
    // instabilidade transitória, sobrescrevia gpsDriveStatus para
    // "expired" por cima do "ok" que o login() já tinha (ou estava
    // prestes a) setar, deixando "Sessão de gestão expirada" preso mesmo
    // com login bem-sucedido — e usePermission (fail-closed) negando tudo
    // até a próxima chance de refetch. `getGpsDriveAccessToken()` não é
    // reativo por si só, mas gpsDriveStatus mudar de "loading" pra
    // "ok"/"expired" já re-renderiza a árvore inteira (AuthProvider fica
    // acima de tudo), reavaliando `enabled` no momento certo.
    enabled: !!getGpsDriveAccessToken(),
  });
}

// Menu Administração > 01 Identidade Visual. GET é aberto a qualquer
// usuário logado (aplicado no app inteiro, ver use-branding-theme.ts) —
// só a mutation abaixo exige a permissão administracao.identidade_visual.
export function useAdminBranding() {
  return useQuery({
    queryKey: queryKeys.adminBranding,
    queryFn: () => gpsDriveAdminBrandingService.get(),
    retry: false,
  });
}

// Tela de Login pública (routes/login.tsx) — sem autenticação, roda antes
// do login. Nunca deixa a tela de login travada esperando: staleTime alto
// (o fundo não muda com frequência) e sem retry agressivo, já que o
// componente já tem seu próprio fallback CSS caso a chamada falhe.
export function usePublicLoginBranding() {
  return useQuery({
    queryKey: queryKeys.publicLoginBranding,
    queryFn: () => gpsDriveAdminBrandingService.getPublicLoginBranding(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useUpdateAdminBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAdminBrandingInput) => gpsDriveAdminBrandingService.update(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

// "Acesso rápido" (tela Início, desktop) — catálogo fechado quase nunca
// muda (é código, não dado de empresa), staleTime alto pra não reconsultar
// toda hora. Self-service, sempre o PRÓPRIO usuário — ver
// gpsdrive-quick-access.service.ts.
export function useQuickAccessCatalog() {
  return useQuery({
    queryKey: queryKeys.quickAccessCatalog,
    queryFn: () => gpsDriveQuickAccessService.getCatalog(),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useMyQuickAccess() {
  return useQuery({
    queryKey: queryKeys.myQuickAccess,
    queryFn: () => gpsDriveQuickAccessService.getMine(),
    retry: false,
  });
}

export function useUpdateMyQuickAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tiles: string[]) => gpsDriveQuickAccessService.updateMine(tiles),
    onSuccess: (data) => qc.setQueryData(queryKeys.myQuickAccess, data),
  });
}

export function useUploadBrandingLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadLogo(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

export function useUploadBrandingLogoDark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadLogoDark(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

export function useUploadBrandingFavicon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadFavicon(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

export function useUploadBrandingLoginLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadLoginLogo(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

export function useUploadBrandingLoginBackgroundImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadLoginBackgroundImage(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

export function useUploadBrandingLoginBackgroundTabletImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadLoginBackgroundTabletImage(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

export function useUploadBrandingLoginBackgroundPwaImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => gpsDriveAdminBrandingService.uploadLoginBackgroundPwaImage(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminBranding }),
  });
}

// Administração > Conta & Integrações > Contrato — modelos de contrato e
// signatários/testemunhas cadastrados pela empresa.
export function useContractTemplates() {
  return useQuery({
    queryKey: queryKeys.contractTemplates,
    queryFn: () => gpsDriveContractsService.listTemplates(),
    retry: false,
  });
}

export function useCreateContractTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertContractTemplateInput) =>
      gpsDriveContractsService.createTemplate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.contractTemplates }),
  });
}

export function useUpdateContractTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpsertContractTemplateInput }) =>
      gpsDriveContractsService.updateTemplate(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.contractTemplates }),
  });
}

export function useDeleteContractTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveContractsService.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.contractTemplates }),
  });
}

export function useContractSignatories() {
  return useQuery({
    queryKey: queryKeys.contractSignatories,
    queryFn: () => gpsDriveContractsService.listSignatories(),
    retry: false,
  });
}

export function useCreateContractSignatory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertContractSignatoryInput) =>
      gpsDriveContractsService.createSignatory(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.contractSignatories }),
  });
}

export function useUpdateContractSignatory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpsertContractSignatoryInput }) =>
      gpsDriveContractsService.updateSignatory(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.contractSignatories }),
  });
}

export function useDeleteContractSignatory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveContractsService.deleteSignatory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.contractSignatories }),
  });
}

// Administração > Comunicação > Mensagem de Boas-Vindas.
export function useWelcomeMessage() {
  return useQuery({
    queryKey: queryKeys.welcomeMessage,
    queryFn: () => gpsDriveWelcomeMessageService.get(),
    retry: false,
  });
}

export function useUpdateWelcomeMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWelcomeMessageInput) => gpsDriveWelcomeMessageService.update(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.welcomeMessage }),
  });
}

// Administração > Conta & Integrações > Planos — fonte real de preço da
// aba FATURAMENTO (Nova Recorrência, ver users.tsx).
export function usePlans(enabled = true) {
  return useQuery({
    queryKey: queryKeys.plans,
    queryFn: () => gpsDrivePlansService.list(),
    enabled,
    retry: false,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertPlanInput) => gpsDrivePlansService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.plans }),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpsertPlanInput }) =>
      gpsDrivePlansService.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.plans }),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDrivePlansService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.plans }),
  });
}

// Administração > Conta & Integrações > Comandos GT06 — catálogo por
// empresa usado como atalho extra no popup "Enviar comando"
// (VehicleSendCommandDialog).
export function useGt06Commands(enabled = true) {
  return useQuery({
    queryKey: queryKeys.gt06Commands,
    queryFn: () => gt06CommandsService.list(),
    enabled,
    retry: false,
  });
}

export function useCreateGt06Command() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertGt06CommandInput) => gt06CommandsService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.gt06Commands }),
  });
}

export function useUpdateGt06Command() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpsertGt06CommandInput }) =>
      gt06CommandsService.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.gt06Commands }),
  });
}

export function useDeleteGt06Command() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gt06CommandsService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.gt06Commands }),
  });
}

// Cadastro rápido de protocolo (pedido 2026-09-16) — opção "Outro" na aba
// Hardware e Simcard do cadastro de veículo. Qualquer usuário autenticado
// pode chamar (POST /tracker-protocols/quick, ver serviço), diferente do
// useCreateTrackerProtocol de @/hooks/queries/platform (Super Master).
// Invalida a MESMA trackerProtocolsKey pra reaparecer tanto no Select do
// próprio cadastro quanto depois em Administração > Comandos GT06.
export function useQuickCreateTrackerProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => gpsDriveTrackerProtocolsService.createQuick(label),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerProtocolsKey }),
  });
}

// "Registrar Assistência" (PWA, menu Perfil) + "Configurar Assistência"
// (Administração → Comunicação) — ver AssistanceController no
// gpsdrive-backend. Mesma permissão granular dos dois lados
// (administracao.assistencia).
export function useServiceTypes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.assistanceServiceTypes,
    queryFn: () => gpsDriveAssistanceService.listServiceTypes(),
    enabled,
    retry: false,
  });
}

export function useCreateServiceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServiceTypeInput) => gpsDriveAssistanceService.createServiceType(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.assistanceServiceTypes }),
  });
}

export function useUpdateServiceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceTypeInput }) =>
      gpsDriveAssistanceService.updateServiceType(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.assistanceServiceTypes }),
  });
}

// Grupos de assistência (pedido 2026-09-11), ex.: "Assistência 100km" —
// organizam a tela Configurar Assistência em seções e são o que um Plano
// "com assistência" vincula (ver usePlans acima).
export function useServiceGroups(enabled = true) {
  return useQuery({
    queryKey: queryKeys.assistanceServiceGroups,
    queryFn: () => gpsDriveAssistanceService.listServiceGroups(),
    enabled,
    retry: false,
  });
}

export function useCreateServiceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertServiceGroupInput) => gpsDriveAssistanceService.createServiceGroup(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.assistanceServiceGroups }),
  });
}

export function useUpdateServiceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpsertServiceGroupInput }) =>
      gpsDriveAssistanceService.updateServiceGroup(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.assistanceServiceGroups }),
  });
}

export function useDeleteServiceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveAssistanceService.deleteServiceGroup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.assistanceServiceGroups });
      qc.invalidateQueries({ queryKey: queryKeys.assistanceServiceTypes });
      qc.invalidateQueries({ queryKey: queryKeys.plans });
    },
  });
}

// Painel "Situação financeira" ao escolher o cliente em Registrar
// Assistência — resolve/provisiona o cliente e já devolve o status
// derivado (nunca chamado sem um traccarUserId escolhido).
export function useAssistanceClientSummary(traccarUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.assistanceClientSummary(traccarUserId ?? 0),
    queryFn: () => gpsDriveAssistanceService.getClientSummary(traccarUserId as number),
    enabled: !!traccarUserId,
    retry: false,
  });
}

// Uso ATUAL (limite anual + intervalo mínimo) de um serviço específico
// pra este (cliente, veículo) — pedido 2026-09-11, substitui
// useLastAssistanceRequest (aviso-só, removido). canRequest:false já
// reflete o mesmo bloqueio real que o backend aplica em POST /requests —
// usado pra desabilitar o botão de registrar ANTES de tentar, não só
// mostrar um aviso.
export function useServiceUsage(
  traccarUserId: number | undefined,
  deviceId: number | undefined,
  serviceTypeId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.assistanceUsage(traccarUserId ?? 0, deviceId ?? 0, serviceTypeId ?? ""),
    queryFn: () =>
      gpsDriveAssistanceService.getUsage(traccarUserId as number, deviceId as number, serviceTypeId as string),
    enabled: !!traccarUserId && !!deviceId && !!serviceTypeId,
    retry: false,
  });
}

export function useCreateAssistanceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssistanceRequestInput) => gpsDriveAssistanceService.createRequest(input),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.assistanceUsage(
          variables.traccarUserId,
          variables.deviceId,
          variables.serviceTypeId,
        ),
      });
    },
  });
}

// "Minhas Assistências" (PWA, menu Perfil) — histórico do próprio cliente
// logado, sem permissão nenhuma (self-scoped no backend).
export function useMyAssistanceRequests(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myAssistanceRequests,
    queryFn: () => gpsDriveAssistanceService.myRequests(),
    enabled,
    retry: false,
  });
}

// Aba FATURAMENTO (Editar Usuário) — cobranças reais do cliente no Asaas.
const INVOICES_PAGE_SIZE = 12;

export function useInvoices(userId: string | undefined, offset: number) {
  return useQuery({
    queryKey: queryKeys.invoices(userId ?? "", offset),
    queryFn: () =>
      gpsDriveBillingService.list(userId as string, { limit: INVOICES_PAGE_SIZE, offset }),
    enabled: !!userId,
    retry: false,
  });
}

// Tela Financeiro do app do cliente (PWA) — a ÚNICA fonte de verdade sobre
// acesso suspenso (ver BillingStatus/BillingService#getMyStatus). Poll
// curto de propósito: a reativação (regra 3 do pedido 2026-08-28) precisa
// acontecer sozinha assim que o Asaas confirmar o pagamento, sem o usuário
// ter que sair e voltar a logar — nunca decide "liberado" só pelo clique
// em "Já paguei", sempre pelo que este endpoint devolver.
export function useMyBillingStatus(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myBillingStatus,
    queryFn: () => gpsDriveBillingService.myStatus(),
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: CreateInvoiceInput }) =>
      gpsDriveBillingService.create(userId, data),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["invoices", vars.userId] }),
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      invoiceId,
      data,
    }: {
      userId: string;
      invoiceId: string;
      data: UpdateInvoiceInput;
    }) => gpsDriveBillingService.update(userId, invoiceId, data),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["invoices", vars.userId] }),
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, invoiceId }: { userId: string; invoiceId: string }) =>
      gpsDriveBillingService.remove(userId, invoiceId),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["invoices", vars.userId] }),
  });
}

// A assinatura criada aqui não aparece na tabela na hora (o Asaas ainda não
// gerou a primeira cobrança) — mesmo assim invalida a lista de faturas,
// pra quando o operador reabrir a aba já pegar a cobrança gerada.
export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: CreateSubscriptionInput }) =>
      gpsDriveBillingService.createSubscription(userId, data),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["invoices", vars.userId] }),
  });
}

// Botão "Pagar com PIX" (Faturas do app do cliente) — mutation em vez de
// query porque só deve rodar quando o cliente pede (a chamada gera/consulta
// o QR code no Asaas a cada clique, não é algo pra manter em cache/poll).
export function usePixQrCode() {
  return useMutation({
    mutationFn: ({ userId, invoiceId }: { userId: string; invoiceId: string }) =>
      gpsDriveBillingService.pixQrCode(userId, invoiceId),
  });
}

// Cria um Grupo novo com um conjunto de permissões próprio (botão "Criar
// grupo" na aba PERMISSÕES). Escreve o Grupo recém-criado direto no cache
// (setQueryData) em vez de só invalidar e esperar um refetch — o select
// "Grupo" atualiza na hora, sem depender de um round-trip de rede extra
// nem do timing de invalidação (bug real 2026-08-27: grupo criado no
// banco corretamente, mas o select só refletia depois de recarregar a
// página). invalidateQueries continua rodando depois, como reconciliação
// com o servidor — nunca a única fonte da atualização imediata.
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      scope,
      permissions,
    }: {
      name: string;
      scope: RoleScope;
      permissions: PermissionCategoryState[];
    }) => gpsDriveRolesService.create(name, scope, permissions),
    onSuccess: (created) => {
      qc.setQueryData<GpsDriveRole[]>(queryKeys.gpsDriveRoles, (prev) =>
        prev
          ? [...prev.filter((r) => r.id !== created.id), created].sort((a, b) =>
              a.name.localeCompare(b.name),
            )
          : [created],
      );
      qc.invalidateQueries({ queryKey: queryKeys.gpsDriveRoles });
    },
  });
}

// Editar/excluir Grupo (pedido 2026-09-03) — mesmo padrão de invalidação
// de useCreateRole. Grupo "Administrator" é protegido no backend (422).
export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      name,
      scope,
      permissions,
    }: {
      id: string;
      name: string;
      scope: RoleScope;
      permissions: PermissionCategoryState[];
    }) => gpsDriveRolesService.update(id, name, scope, permissions),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.gpsDriveRoles }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveRolesService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.gpsDriveRoles }),
  });
}

// "Grupo do usuário" (pedido 2026-09-03) — independente de
// useChangeUserRole acima. Sem onSuccess próprio: `identity` é cacheado
// por traccarUserId (ver queryKeys.gpsDriveUserIdentity), que este
// mutationFn não tem (só o userId interno) — quem chama invalida
// explicitamente com o traccarUserId que já tem em mãos (ver
// handleChangeAccountType em users.tsx).
export function useChangeAccountType() {
  return useMutation({
    mutationFn: ({
      userId,
      accountType,
    }: {
      userId: string;
      accountType: Exclude<AccountType, "revenda">;
    }) => gpsDriveUsersService.changeAccountType(userId, accountType),
  });
}

// "Situação" (Suspender/Reativar, coluna da tabela) — invalida
// "users-summary" por inteiro (sem o id-list join da queryKey exata, ver
// queryKeys.usersSummary) pra badge da linha atualizar na hora, já que o
// `suspended` exibido vem do resumo em lote, não de useUsers().
export function useSetUserSuspended() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, suspended }: { userId: string; suspended: boolean }) =>
      gpsDriveUsersService.setSuspended(userId, suspended),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users-summary"] }),
  });
}

// Revenda (pedido 2026-09-07) — empresa do próprio usuário autenticado, só
// pra decidir se a opção "Revenda" aparece no popup de novo usuário (ver
// CreateUserDialog em users.tsx). staleTime alto: parentCompanyId nunca
// muda sozinho depois de criado.
export function useMyCompany() {
  return useQuery({
    queryKey: queryKeys.myCompany,
    queryFn: () => gpsDriveUsersService.getMyCompany(),
    enabled: !!GPSDRIVE_API_URL,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Câmera da frota (pedido 2026-09-16) — veículos da empresa com IMEI de
// câmera cadastrado (ver VehicleFormPanel em routes/index.tsx). Backend já
// nega 403 se o módulo Câmera não estiver ativo ou o usuário for cliente
// (ver fleet-camera.controller.ts#requireCameraAccess) — retry:false pra
// não martelar o backend numa negação esperada.
export function useFleetCameraVehicles() {
  return useQuery({
    queryKey: ["fleet-camera", "vehicles"],
    queryFn: () => fleetCameraService.listVehicles(),
    enabled: !!GPSDRIVE_API_URL,
    retry: false,
  });
}

// Mesmo raciocínio de useJt808CameraPhotos (painel Super Master): a lista
// pode crescer a qualquer momento (novo alarme), prefere refetch a mostrar
// lista velha.
// Alertas da câmera — atualiza sozinho a cada 20 s com a tela aberta.
export function useFleetCameraAlarms(deviceId: number | null) {
  return useQuery({
    queryKey: ["fleet-camera", deviceId, "alarms"],
    queryFn: () => fleetCameraService.listAlarms(deviceId!),
    enabled: !!GPSDRIVE_API_URL && deviceId != null,
    refetchInterval: 20_000,
    retry: false,
  });
}

export function useFleetCameraPhotos(deviceId: number | null) {
  return useQuery({
    queryKey: ["fleet-camera", deviceId, "photos"],
    queryFn: () => fleetCameraService.listPhotos(deviceId!),
    enabled: !!GPSDRIVE_API_URL && deviceId != null,
    retry: false,
  });
}

// Cria a empresa filha de revenda + o primeiro Administrator dela — ver
// CreateUserDialog#handleSave em users.tsx (chamado no lugar de
// useChangeAccountType quando accountType === "revenda").
export function useCreateReseller() {
  return useMutation({
    mutationFn: (input: CreateResellerInput) => gpsDriveUsersService.createReseller(input),
  });
}

// Troca o Grupo (Role) do usuário — decisão 2026-08-27: selecionar um
// Grupo agora aplica os padrões dele na hora (como "permissões
// favoritas"), substituindo o antigo botão separado "Restaurar permissões
// padrão do grupo" — ver handleChangeRole em users.tsx, que encadeia esta
// mutation com useRestoreDefaultUserPermissions.
export function useChangeUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      gpsDriveUsersService.changeRole(userId, roleId),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: queryKeys.userPermissions(vars.userId) }),
  });
}

// Fotos reais da frota, numa única chamada para todos os veículos
// carregados (nunca um GET por card — ver GET /vehicle-metadata/images no
// backend). Devolve um Record<deviceId, imageUrls> só com quem tem foto;
// quem não aparece cai no fallback de ícone no componente que consome.
// Desabilitado sem VITE_GPSDRIVE_API_URL configurada (GPS Drive Backend
// ainda não implantado) — evita bater numa URL relativa/inválida a cada
// refetch da lista. Precisa ser um objeto plano, NUNCA um Map: o cache do
// React Query agora persiste em localStorage via JSON.stringify (pedido
// 2026-09-03, ver __root.tsx/PersistQueryClientProvider) — um Map vira "{}"
// nesse round-trip (JSON não serializa Map), e o próximo ".get(id)" quebra
// com "X.get is not a function" (bug real 2026-09-03, reportado logo depois
// de ativar a persistência).
export function useVehicleImages(deviceIds: string[], enabled = true) {
  return useQuery({
    queryKey: queryKeys.vehicleImages(deviceIds),
    queryFn: async () => {
      const entries = await vehicleMetadataService.getImages(deviceIds);
      const record: Record<string, VehicleImageUrls> = {};
      for (const entry of entries) record[String(entry.traccarDeviceId)] = entry.imageUrls;
      writeVehicleImagesCache(record);
      return record;
    },
    enabled: deviceIds.length > 0 && !!GPSDRIVE_API_URL && enabled,
    staleTime: 60_000,
    retry: false,
    // Página da lista mudou (troca de página, veículo mudou de status e de
    // posição): mantém as fotos já carregadas enquanto busca as novas, em
    // vez de piscar o ícone genérico (relatado 2026-10-03).
    placeholderData: keepPreviousData,
    // Hidrata com a última foto conhecida (localStorage) pra evitar o flash
    // do ícone genérico no F5 — initialDataUpdatedAt: 0 garante que isso
    // conta como stale, então o refetch de verdade dispara igual mesmo
    // assim, só não deixa a tela vazia enquanto ele corre.
    initialData: () => {
      if (deviceIds.length === 0) return undefined;
      const cached = readVehicleImagesCache();
      const record: Record<string, VehicleImageUrls> = {};
      let hasAny = false;
      for (const id of deviceIds) {
        if (cached[id]) {
          record[id] = cached[id];
          hasAny = true;
        }
      }
      return hasAny ? record : undefined;
    },
    initialDataUpdatedAt: 0,
  });
}

// Rastreador/simcard reais (GPS Drive Backend) para a listagem da frota —
// mesmo padrão em lote de useVehicleImages, nunca um GET por linha. IMEI
// não vem daqui, sempre do Traccar (ver comentário em vehicles.tsx). Objeto
// plano pelo mesmo motivo de useVehicleImages acima — nunca um Map aqui.
export function useVehicleMetadataSummaries(deviceIds: string[], enabled = true) {
  return useQuery({
    queryKey: queryKeys.vehicleMetadataSummaries(deviceIds),
    queryFn: async () => {
      const entries = await vehicleMetadataService.getSummaries(deviceIds);
      const record: Record<string, (typeof entries)[number]> = {};
      for (const entry of entries) record[String(entry.traccarDeviceId)] = entry;
      return record;
    },
    enabled: deviceIds.length > 0 && !!GPSDRIVE_API_URL && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// Busca de veículo por cliente/linha/ICCID/chassi (2026-10-01) — índice
// da empresa inteira numa chamada só. Só equipe: quem chama passa
// enabled=false pra cliente (o backend devolveria 403). Não importa
// useAuth aqui porque auth-context já importa este arquivo (ciclo).
export function useVehicleSearchIndex(enabled: boolean) {
  return useQuery({
    queryKey: ["vehicle-search-index"],
    queryFn: () => vehicleMetadataService.getSearchIndex(),
    enabled: enabled && !!GPSDRIVE_API_URL,
    staleTime: 2 * 60_000,
    retry: false,
  });
}

// Revenda (pedido 2026-09-07) — etiqueta "Revenda: <nome>" na lista/mapa
// (VehicleFlags em routes/index.tsx), mesmo padrão em lote de
// useVehicleMetadataSummaries. Objeto plano (Record), nunca um Map, pelo
// mesmo motivo documentado em useVehicleImages acima (persistência via
// JSON quebra .get()). Vazio pra qualquer empresa sem filhas — custo
// desprezível de manter habilitado sempre.
export function useVehicleResellerCompanyMap(deviceIds: string[], enabled = true) {
  return useQuery({
    queryKey: queryKeys.vehicleResellerCompanyMap(deviceIds),
    queryFn: async () => {
      const entries = await vehicleMetadataService.getResellerCompanyMap(deviceIds);
      const record: Record<string, (typeof entries)[number]> = {};
      for (const entry of entries) record[String(entry.traccarDeviceId)] = entry;
      return record;
    },
    enabled: deviceIds.length > 0 && !!GPSDRIVE_API_URL && enabled,
    staleTime: 60_000,
    retry: false,
    // Mesmo motivo de useVehicleImages: etiqueta de revenda não some e
    // volta quando a composição da página muda.
    placeholderData: keepPreviousData,
  });
}

export function useUpdateVehicleMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, patch }: { deviceId: string; patch: UpdateVehicleMetadataInput }) =>
      vehicleMetadataService.update(deviceId, patch),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.vehicleMetadata(vars.deviceId) });
      // Tipo de rastreador pode ter virado/deixado de ser "tag" — regra de
      // status de 24h da Tag (vehicles.service.ts) vale na hora.
      if (vars.patch.trackerType !== undefined) void vehiclesService.refreshTagDeviceIds();
    },
  });
}

// Lixeira de veículos (pedido 2026-09-11) — ver gpsdrive-vehicle-trash.service.ts.
export function useVehicleTrash(enabled = true) {
  return useQuery({
    queryKey: queryKeys.vehicleTrash,
    queryFn: () => gpsDriveVehicleTrashService.list(),
    enabled,
    retry: false,
  });
}

// Substitui o DELETE direto no Traccar em "Excluir dispositivo" (ver
// TabExcluir em routes/index.tsx) — grava o retrato antes de excluir de
// verdade.
export function useMoveVehicleToTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (traccarDeviceId: number) => gpsDriveVehicleTrashService.moveToTrash(traccarDeviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vehicles });
      qc.invalidateQueries({ queryKey: queryKeys.vehicleTrash });
    },
  });
}

export function useRestoreVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveVehicleTrashService.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vehicles });
      qc.invalidateQueries({ queryKey: queryKeys.vehicleTrash });
    },
  });
}

export function useCommands(vehicleId?: string) {
  return useQuery({
    queryKey: queryKeys.commands(vehicleId),
    queryFn: () => commandsService.list(vehicleId),
    retry: false,
  });
}

export function useSendCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, type }: { vehicleId: string; type: CommandType }) =>
      commandsService.send(vehicleId, type),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.commands(vars.vehicleId) });
      qc.invalidateQueries({ queryKey: queryKeys.vehicles });
    },
  });
}

// Tipos de comando reais suportados pelo protocolo do dispositivo — popup
// "Enviar comando" (novo). `deviceId` é o id numérico do Traccar (Vehicle.id
// convertido).
export function useCommandTypes(deviceId: number | undefined) {
  return useQuery({
    queryKey: ["command-types", deviceId ?? 0],
    queryFn: () => commandsService.types(deviceId as number),
    enabled: !!deviceId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Capacidades do servidor Traccar (ex.: gateway de SMS configurado) — muda
// raramente, então fica em cache por mais tempo.
export function useServerCapabilities() {
  return useQuery({
    queryKey: ["server-capabilities"],
    queryFn: () => commandsService.getServerCapabilities(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Status/saldo de SMS da PRÓPRIA empresa (GPS Drive Backend) — decide se o
// canal SMS fica disponível no popup "Enviar comando", substituindo
// useServerCapabilities/server.textEnabled (Traccar) para esse canal.
export function useSmsAccountStatus(enabled = true) {
  return useQuery({
    queryKey: ["sms-account-status"],
    queryFn: () => smsSendService.getOwnAccount(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSendSmsCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendSmsCommandInput) => smsSendService.send(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms-account-status"] }),
  });
}

// Combina saldo/ativação de SMS + catálogo GT06 no formato que
// sendBlockCommandWithSmsFallback espera — evita repetir essa derivação nos
// 3 pontos reais de envio de Bloquear/Desbloquear (dialog, painel desktop,
// PWA mobile).
// `protocol` é sempre o do CADASTRO GPS Drive (vehicle_metadata.protocol,
// pedido 2026-09-07) — nunca vehicle.protocol (auto-detectado pelo Traccar,
// pode estar ausente/desatualizado). Só considera comandos do MESMO
// protocolo pra decidir o fallback — uma frota mista pode ter 1 "Bloquear
// via SMS" por protocolo (ver @@unique em Gt06Command no backend).
export function useBlockCommandSmsFallback(
  protocol: string | null | undefined,
  trackerModel?: string | null,
): SmsFallbackContext {
  const { data: smsAccount } = useSmsAccountStatus();
  const { data: gt06Commands } = useGt06Commands();
  const effectiveProtocol = protocol || defaultProtocolForTrackerModel(trackerModel);
  const matching = gt06Commands?.filter((c) => c.protocol === effectiveProtocol) ?? [];
  return {
    smsEnabled: !!smsAccount?.enabled,
    smsOnly: isSmsOnlyTrackerModel(trackerModel),
    blockGt06CommandId: matching.find((c) => c.smsFallbackRole === "block")?.id ?? null,
    unblockGt06CommandId: matching.find((c) => c.smsFallbackRole === "unblock")?.id ?? null,
  };
}

export function useSendRawCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      deviceId,
      type,
      attributes,
      textChannel,
    }: {
      deviceId: number;
      type: string;
      attributes: Record<string, unknown>;
      textChannel: boolean;
    }) => commandsService.sendRaw(deviceId, type, attributes, textChannel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vehicles });
    },
  });
}

export function useGeofences(enabled = true) {
  return useQuery({
    queryKey: queryKeys.geofences,
    queryFn: () => geofencesService.list(),
    enabled,
    retry: false,
  });
}

// Ao editar, busca a geocerca fresca no Traccar (GET /api/geofences/{id})
// em vez de reaproveitar só o dado já em cache da listagem.
export function useGeofence(id: number | undefined) {
  return useQuery({
    queryKey: queryKeys.geofence(id ?? 0),
    queryFn: () => geofencesService.get(id as number),
    enabled: !!id,
    retry: false,
  });
}

export function useCreateGeofence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<import("@/types").Geofence>) => geofencesService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.geofences }),
  });
}

export function useUpdateGeofence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<import("@/types").Geofence> }) =>
      geofencesService.update(id, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.geofences });
      qc.invalidateQueries({ queryKey: queryKeys.geofence(vars.id) });
    },
  });
}

export function useDeleteGeofence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => geofencesService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.geofences }),
  });
}

// Dispositivos já vinculados a uma geocerca (GET /api/geofences?deviceId=
// por dispositivo, ver permissions.service.ts).
export function useGeofenceDevices(
  geofenceId: number | undefined,
  deviceIds: number[],
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.geofenceDevices(geofenceId ?? 0),
    queryFn: () => permissionsService.devicesForGeofence(geofenceId as number, deviceIds),
    enabled: enabled && !!geofenceId && deviceIds.length > 0,
    retry: false,
  });
}

// --- Âncora (pedido 2026-09-04) -----------------------------------------
//
// "Âncora" não é um recurso novo no backend — é composição de duas peças já
// reais e implantadas (motor de alertas Fase 3, ver alert-engine.ts no
// gpsdrive-backend): uma geocerca círculo (CIRCLE, mesmo formato WKT do
// desenho manual em Cercas Virtuais) centrada na posição do veículo no
// momento em que a âncora é ativada, mais uma AlertRule type="geofence"
// nessa cerca, só para aquele device, triggerWhen="exit" e actionKind
// "notify" (só alerta) ou "block" (alerta + engineStop de verdade, enviado
// pelo próprio backend a cada tick do poller — funciona mesmo com o app
// fechado, diferente do Bloquear/Desbloquear manual que depende do
// navegador). Nenhum campo novo em lugar nenhum: os dois recursos usam este
// prefixo fixo no nome só para serem localizados depois via
// alertRulesService.listRules(deviceId) — nunca colide com geocerca/alerta
// criado manualmente pelo operador em Cercas Virtuais.
export const ANCHOR_NAME_PREFIX = "⚓ Âncora — ";

export interface VehicleAnchor {
  rule: AlertRule;
  geofence: Geofence;
  radiusMeters: number;
  actionKind: ActionKind;
}

export function useVehicleAnchor(deviceId: number | undefined) {
  const rulesQuery = useAlertRules(deviceId);
  const anchorRule = rulesQuery.data?.find(
    (r) => r.type === "geofence" && r.geofenceId !== null && r.name?.startsWith(ANCHOR_NAME_PREFIX),
  );
  const geofenceQuery = useGeofence(anchorRule?.geofenceId ?? undefined);
  const parsed = geofenceQuery.data ? parseWkt(geofenceQuery.data.area) : null;
  const anchor: VehicleAnchor | undefined =
    anchorRule && geofenceQuery.data && parsed?.type === "circle"
      ? {
          rule: anchorRule,
          geofence: geofenceQuery.data,
          radiusMeters: parsed.data.radius,
          actionKind: anchorRule.actionKind,
        }
      : undefined;
  return {
    anchor,
    // isFetched (não isLoading): só interessa a PRIMEIRA resolução — uma
    // regra existente nunca "some" durante um refetch de fundo.
    isFetched: rulesQuery.isFetched && (!anchorRule || geofenceQuery.isFetched),
  };
}

// Aplica só a diferença entre o vínculo atual e o selecionado — nunca apaga
// tudo para recriar. Mesmo padrão de useSyncUserDevicePermissions.
export function useSyncGeofenceDevices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      geofenceId,
      link,
      unlink,
    }: {
      geofenceId: number;
      link: number[];
      unlink: number[];
    }) => {
      // allSettled (não all): mesmo se um POST/DELETE falhar, os outros que já
      // tiverem sucesso no servidor não podem ficar escondidos atrás de uma
      // falha parcial — por isso a invalidação roda sempre (onSettled), e só
      // depois agregamos os erros, se houver.
      const results = await Promise.allSettled([
        ...link.map((deviceId) => permissionsService.linkDeviceGeofence(deviceId, geofenceId)),
        ...unlink.map((deviceId) => permissionsService.unlinkDeviceGeofence(deviceId, geofenceId)),
      ]);
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed.length) {
        throw new Error(
          `${failed.length} de ${results.length} vínculo(s) de veículo falharam ao sincronizar`,
        );
      }
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.geofences });
      qc.invalidateQueries({ queryKey: queryKeys.vehicles });
      qc.invalidateQueries({ queryKey: queryKeys.geofenceDevices(vars.geofenceId) });
    },
  });
}

export function useNotificationTypes() {
  return useQuery({
    queryKey: queryKeys.notificationTypes,
    queryFn: () => notificationsService.listTypes(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useDeviceNotifications(deviceId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.deviceNotifications(deviceId ?? 0),
    queryFn: () => notificationsService.listByDevice(deviceId as number),
    enabled: !!deviceId,
    retry: false,
  });
}

export function useVehicleTrips(
  vehicleId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.trips(vehicleId ?? "", from, to),
    queryFn: () => reportsService.trips(vehicleId as string, from, to),
    enabled: enabled && !!vehicleId,
    retry: false,
  });
}

export function useVehicleRoute(
  vehicleId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.route(vehicleId ?? "", from, to),
    queryFn: () => reportsService.route(vehicleId as string, from, to),
    enabled: enabled && !!vehicleId,
    retry: false,
  });
}

export function useVehicleEvents(
  vehicleId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.events(vehicleId ?? "", from, to),
    queryFn: () => reportsService.events(vehicleId as string, from, to),
    enabled: enabled && !!vehicleId,
    retry: false,
  });
}

// Feed "Eventos" do app do cliente (frota inteira, não um veículo só) —
// janela deslizante das últimas `hours` horas a partir de agora, com
// refetch curto porque a aba fica aberta olhando pra isso em tempo real
// (mesmo espírito do poll de 15s de useVehicles, só que mais espaçado por
// ser um relatório, não a lista principal).
//
// `active` (default true) — pedido 2026-09-08 "app lento, demora pra
// entrar em qualquer coisa": o EventosPanel do PWA fica SEMPRE montado
// (ver comentário em mobile-app.tsx sobre não desmontar abas), então sem
// essa flag esse fetch de frota inteira rodava a cada 30s pra sempre,
// mesmo com o cliente numa aba totalmente diferente — puro desperdício de
// rede/CPU competindo com qualquer clique (achado idêntico ao flood do
// sticky-seed, ver gpsdrive_sticky_seed_flood_fix_2026-09-04 na memória,
// que também documentou esse MESMO endpoint devolvendo 414 pra frotas
// grandes). Passe `active: activeTab === "eventos"` pra só buscar/repetir
// enquanto a aba estiver de fato visível.
export function useRecentEvents(vehicleIds: string[], hours = 72, active = true) {
  const to = new Date().toISOString();
  const from = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
  return useQuery({
    queryKey: queryKeys.reportKey("fleet-events", vehicleIds, from.slice(0, 13), to.slice(0, 13)),
    queryFn: () => reportsService.allEvents(vehicleIds, from, to),
    enabled: vehicleIds.length > 0 && active,
    refetchInterval: active ? 30_000 : false,
    retry: false,
  });
}

type MotionSince = { type: "deviceMoving" | "deviceStopped"; since: string } | null;

// "Desde quando" o veículo está no estado de movimento atual — usado no
// popup do mapa e no card para "🕒 Parado há X" / "Em movimento há X". Vem
// do evento real mais recente (deviceStopped/deviceMoving) do Traccar — a
// última posição recebida não serve pra isso porque continua se
// atualizando mesmo com o veículo parado, o que faria o tempo parecer
// sempre "agora".
//
// Alguns rastreadores mandam um "check-in" periódico que o Traccar
// interpreta como deviceMoving mesmo com o veículo fisicamente parado
// (confirmado em produção num GT06: a própria posição referenciada pelo
// evento tinha motion=false e velocidade 0) — isso fazia "Parado há X"
// reiniciar a cada ciclo falso, mesmo o veículo estando parado desde muito
// antes. Por isso cada candidato a "última transição" é conferido contra o
// motion/velocidade real da posição que o Traccar associou a ele; um par
// deviceMoving→deviceStopped que não bate com dado real é tratado como
// ruído e ignorado, continuando a busca mais para trás no tempo.
// Último "parado/em movimento desde" conhecido por veículo (localStorage,
// 2026-10-03): cada cartão parado da lista do mapa busca 14 dias de eventos
// — no F5 o "• há X" aparecia um cartão de cada vez. Mostra o último valor
// na hora e o refetch corrige por trás (initialDataUpdatedAt: 0 = stale).
const MOTION_SINCE_KEY = "gpsdrive.motionSince.v1";
let motionSinceCache: Record<string, MotionSince> = {};
try {
  motionSinceCache = JSON.parse(localStorage.getItem(MOTION_SINCE_KEY) ?? "{}") as Record<string, MotionSince>;
} catch {
  motionSinceCache = {};
}
function rememberMotionSince(vehicleId: string, value: MotionSince) {
  const prev = motionSinceCache[vehicleId];
  if (prev?.type === value?.type && prev?.since === value?.since) return;
  motionSinceCache[vehicleId] = value;
  try {
    localStorage.setItem(MOTION_SINCE_KEY, JSON.stringify(motionSinceCache));
  } catch {
    // quota/anônimo — fica só em memória.
  }
}

export function useVehicleMotionSince(vehicleId: string | undefined, enabled = true) {
  const computeMotionSince = async (): Promise<MotionSince> => {
    const to = new Date();
    const from = new Date(to.getTime() - 14 * 24 * 60 * 60 * 1000);
    const events = await reportsService.events(
      vehicleId as string,
      from.toISOString(),
      to.toISOString(),
    );
    const motionEvents = events
      .filter((e) => e.type === "deviceMoving" || e.type === "deviceStopped")
      .sort((a, b) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime());
    if (motionEvents.length === 0) return null;

    const positionIds = motionEvents
      .map((e) => (e.attributes as { positionId?: number } | undefined)?.positionId)
      .filter((id): id is number => typeof id === "number");
    const positionsById = await positionsService.byIds([...new Set(positionIds)]);

    const isRealMotionEvent = (e: (typeof motionEvents)[number]): boolean => {
      const positionId = (e.attributes as { positionId?: number } | undefined)?.positionId;
      const pos = typeof positionId === "number" ? positionsById.get(positionId) : undefined;
      if (!pos || typeof pos.motion !== "boolean") return true; // sem dado pra conferir, confia no evento
      const expectsMoving = e.type === "deviceMoving";
      return pos.motion === expectsMoving || pos.speed > 0;
    };

    for (let i = 0; i < motionEvents.length; i++) {
      const ev = motionEvents[i];
      if (ev.type === "deviceMoving") {
        if (isRealMotionEvent(ev)) return { type: "deviceMoving", since: ev.eventTime };
        continue; // "em movimento" falso — ignora e olha o evento mais antigo
      }
      const prevMoving = motionEvents[i + 1];
      if (!prevMoving || prevMoving.type !== "deviceMoving" || isRealMotionEvent(prevMoving)) {
        return { type: "deviceStopped", since: ev.eventTime };
      }
      i++; // par moving/stopped inteiro é ruído — pula os dois
    }
    return null;
  };
  return useQuery({
    queryKey: ["vehicle-motion-since", vehicleId ?? ""],
    initialData: () => (vehicleId && vehicleId in motionSinceCache ? motionSinceCache[vehicleId] : undefined),
    initialDataUpdatedAt: 0,
    queryFn: async (): Promise<MotionSince> => {
      const result = await computeMotionSince();
      if (vehicleId) rememberMotionSince(vehicleId, result);
      return result;
    },
    enabled: enabled && !!vehicleId,
    staleTime: 45_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

// Versão detalhada de useVehicleRoute — para o Centro de Histórico (tabela
// e player), que precisa de bem mais campos por posição.
export function useVehicleRoutePositions(
  vehicleId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.routePositions(vehicleId ?? "", from, to),
    queryFn: () => reportsService.routePositions(vehicleId as string, from, to),
    enabled: enabled && !!vehicleId,
    retry: false,
  });
}

export function useVehicleSummary(
  vehicleId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.summary(vehicleId ?? "", from, to),
    queryFn: () => reportsService.summary(vehicleId as string, from, to),
    enabled: enabled && !!vehicleId,
    retry: false,
  });
}

export function useVehicleStops(
  vehicleId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.stops(vehicleId ?? "", from, to),
    queryFn: () => reportsService.stops(vehicleId as string, from, to),
    enabled: enabled && !!vehicleId,
    retry: false,
  });
}

// "Apagar histórico" (pedido 2026-09-08) — lista ativos + já restaurados
// de um veículo. Usado (a) por RouteHistoryCenter, pra filtrar posição/
// rota do Traccar antes de mostrar (ver history-deletion-filter.ts) e (b)
// por VehicleLogsPanel, pra exibir o log real de apagar/recuperar com o
// botão "Recuperar" (Administrador da empresa).
export function useHistoryDeletions(traccarDeviceId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.historyDeletions(traccarDeviceId ?? 0),
    queryFn: () => historyDeletionsService.listForDevice(traccarDeviceId as number),
    enabled: !!traccarDeviceId,
    retry: false,
  });
}

export function useCreateHistoryDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateHistoryDeletionInput) => historyDeletionsService.create(input),
    onSuccess: (_entry, input) => {
      void qc.invalidateQueries({ queryKey: queryKeys.historyDeletions(input.traccarDeviceId) });
    },
  });
}

export function useRestoreHistoryDeletion(traccarDeviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => historyDeletionsService.restore(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.historyDeletions(traccarDeviceId) });
    },
  });
}

// ---------------------------------------------------------------------
// Tela de Relatórios (/reports) — variantes multi-veículo dos relatórios
// oficiais do Traccar. Só disparam quando `enabled` (acionado pelo botão
// "Gerar o relatório"), e cada combinação de veículos/período tem sua
// própria chave — nunca reaproveita resultado de uma geração anterior com
// outro filtro.
// ---------------------------------------------------------------------

export function useReportTrips(deviceIds: string[], from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-trips", deviceIds, from, to),
    queryFn: () => reportsService.trips(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportRoutePositions(
  deviceIds: string[],
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-route-positions", deviceIds, from, to),
    queryFn: () => reportsService.routePositions(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportRoute(deviceIds: string[], from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-route", deviceIds, from, to),
    queryFn: () => reportsService.route(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportSummary(deviceIds: string[], from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-summary", deviceIds, from, to),
    queryFn: () => reportsService.summary(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportStops(deviceIds: string[], from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-stops", deviceIds, from, to),
    queryFn: () => reportsService.stops(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportEvents(deviceIds: string[], from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-events", deviceIds, from, to),
    queryFn: () => reportsService.events(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportGeofenceEvents(
  deviceIds: string[],
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-geofence-events", deviceIds, from, to),
    queryFn: () => reportsService.geofenceEvents(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportCommandEvents(
  deviceIds: string[],
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-commands", deviceIds, from, to),
    queryFn: () => reportsService.commandEvents(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

// Pedido 2026-09-01 ("tem que retornar o que o rastreador disse"): depois
// de enviar um comando por GPRS, o popup fica de olho por um tempo curto
// num commandResult novo pra este device (a resposta crua que o protocolo
// devolveu, ex.: GT06 "RELAY,1#=Success!" — ver comentário em
// reportsService.commandEvents). O Traccar não relaciona um commandResult a
// qual comando o originou, só ao device e ao horário — por isso `sinceIso`
// (o instante do envio) é o único jeito de decidir "isso é resposta do
// comando que acabei de mandar", pegando sempre o mais recente >= sinceIso.
// refetchInterval só roda enquanto `enabled` (o chamador desliga depois de
// achar uma resposta ou depois do timeout de espera).
export function useLatestCommandResult(
  deviceId: string | undefined,
  sinceIso: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["latest-command-result", deviceId, sinceIso],
    queryFn: async () => {
      const to = new Date().toISOString();
      const events = await reportsService.commandEvents([deviceId as string], sinceIso as string, to);
      const sorted = events
        .filter((e) => e.eventTime >= (sinceIso as string))
        .sort((a, b) => b.eventTime.localeCompare(a.eventTime));
      return sorted[0] ?? null;
    },
    enabled: enabled && !!deviceId && !!sinceIso,
    refetchInterval: enabled ? 4_000 : false,
    retry: false,
  });
}

export function useReportOnOffPeriods(
  deviceIds: string[],
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-onoff", deviceIds, from, to),
    queryFn: () => reportsService.onOffPeriods(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useReportDrivingStopped(
  deviceIds: string[],
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.reportKey("report-driving-stopped", deviceIds, from, to),
    queryFn: () => reportsService.drivingStopped(deviceIds, from, to),
    enabled: enabled && deviceIds.length > 0,
    retry: false,
  });
}

export function useToggleDeviceNotification(deviceId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, enabled }: { type: string; enabled: boolean }) =>
      enabled
        ? notificationsService.enableForDevice(deviceId as number, type)
        : notificationsService.disableForDevice(deviceId as number, type),
    onSuccess: () => {
      if (deviceId) qc.invalidateQueries({ queryKey: queryKeys.deviceNotifications(deviceId) });
    },
  });
}

// Tela global "Notificações" do app do cliente — aplica um tipo a TODA a
// frota de uma vez (em vez de veículo por veículo, como
// useToggleDeviceNotification acima). Sem endpoint de lote no Traccar:
// dispara uma chamada por veículo (Promise.allSettled — um 401/404 isolado
// não deve travar os demais) e invalida cada query individual afetada.
export function useToggleNotificationForAllVehicles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      deviceIds,
      type,
      enabled,
    }: {
      deviceIds: number[];
      type: string;
      enabled: boolean;
    }) => {
      await Promise.allSettled(
        deviceIds.map((deviceId) =>
          enabled
            ? notificationsService.enableForDevice(deviceId, type)
            : notificationsService.disableForDevice(deviceId, type),
        ),
      );
    },
    onSuccess: (_d, vars) => {
      for (const deviceId of vars.deviceIds) {
        qc.invalidateQueries({ queryKey: queryKeys.deviceNotifications(deviceId) });
      }
    },
  });
}

// Administração → Contrato → Dados da empresa (2026-10-14) — cabeçalho do
// contrato. Toda a equipe lê; salvar exige Conta & Integrações.
export function useContractCompany(enabled = true) {
  return useQuery({
    queryKey: queryKeys.contractCompany,
    queryFn: () => gpsDriveContractsService.getCompany(),
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}
